import { Modal, Container, Row, Col, Spinner } from "react-bootstrap";
import { formatCurrency, formatSignedCurrency } from "../utilities/formatCurrency";
import { getOrderCostLines, normalizeOrderCost } from "../lib/orderCost";
import { formatFirebaseDate } from "../utilities/format";
import { useState, useEffect, useCallback, Fragment } from "react";
import { OrderItem } from "../components/OrderItem";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../lib/config/firebaseInit";
import toast from "../utilities/toast";
import OrderSkeleton from "../components/skeletons/OrderSkeleton";
import { useNavigate } from "react-router-dom";
import { fetchOrdersHistory } from "../lib/webService/webService.jsx";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowClockwise,
  X,
  ArrowLeft,
  Clock,
  Receipt,
  Check,
  Truck,
  Box,
  HourglassSplit,
  CartCheck,
  PersonCircle,
} from "react-bootstrap-icons";
import { useShoppingCart } from "../context/ShoppingCartContext";
import { PullToRefreshWrapper } from "../components/pullRefresh";
import { Seo } from "../components/Seo";
import { generateNoIndexSEO } from "../lib/seo/metaGenerators";

// ─── Constants ───────────────────────────────────────────────────────────────

const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", system-ui, sans-serif';

const STATUS_MAP = {
  accepted:   { color: "#007AFF", bg: "rgba(0,122,255,0.10)",   label: "Accepted"   },
  placed:     { color: "#007AFF", bg: "rgba(0,122,255,0.10)",   label: "Placed"     },
  processing: { color: "#FF9500", bg: "rgba(255,149,0,0.10)",   label: "Processing" },
  enroute:    { color: "#5856D6", bg: "rgba(88,86,214,0.10)",   label: "En Route"   },
  delivered:  { color: "#34C759", bg: "rgba(52,199,89,0.10)",   label: "Delivered"  },
};

const getStatus = (status) =>
  STATUS_MAP[status?.toLowerCase()] ?? {
    color: "#8E8E93",
    bg: "rgba(142,142,147,0.12)",
    label: status ?? "Unknown",
  };

// ─── Shared micro-components ─────────────────────────────────────────────────

function IconBtn({ onClick, disabled, children, tint = "rgba(60,60,67,0.08)", color = "#1C1C1E" }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 36, height: 36, borderRadius: "50%",
        border: "none", background: tint,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        flexShrink: 0,
        transition: "opacity 0.15s ease",
        color,
      }}
    >
      {children}
    </button>
  );
}

function StatusPill({ cfg }) {
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center",
        padding: "3px 8px", borderRadius: 7,
        fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.005em",
        color: cfg.color, background: cfg.bg,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.label}
    </span>
  );
}

function SectionLabel({ children }) {
  return (
    <p
      style={{
        margin: "0 0 11px",
        fontSize: "0.6875rem", fontWeight: 600,
        color: "rgba(60,60,67,0.45)",
        textTransform: "uppercase", letterSpacing: "0.06em",
      }}
    >
      {children}
    </p>
  );
}

function Divider() {
  return (
    <div
      style={{ height: 1, background: "rgba(60,60,67,0.07)", margin: "0" }}
    />
  );
}

function CenteredSpinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "55vh" }}>
      <Spinner
        animation="border"
        style={{ width: 26, height: 26, borderWidth: 2, color: "#007AFF" }}
      >
        <span className="visually-hidden">Loading…</span>
      </Spinner>
    </div>
  );
}

// ─── Empty / unauthenticated state ───────────────────────────────────────────

function EmptyState({ icon, title, body, cta, onCta }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.32 }}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", textAlign: "center",
        padding: "0 20px", minHeight: "60vh", gap: 0,
        fontFamily: FONT,
      }}
    >
      <div
        style={{
          width: 64, height: 64, borderRadius: "50%",
          background: "rgba(0,122,255,0.08)",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 18,
        }}
      >
        {icon}
      </div>
      <h2
        style={{
          margin: "0 0 7px",
          fontSize: "1.2rem", fontWeight: 700, color: "#1C1C1E",
          letterSpacing: "-0.024em",
        }}
      >
        {title}
      </h2>
      <p
        style={{
          margin: "0 0 32px",
          fontSize: "0.875rem", color: "rgba(60,60,67,0.55)",
          maxWidth: 230, lineHeight: 1.45,
        }}
      >
        {body}
      </p>
      <button
        onClick={onCta}
        style={{
          width: "100%", maxWidth: 252, padding: "13px 24px",
          borderRadius: 14, border: "none",
          background: "#007AFF", color: "#fff",
          fontSize: "0.9375rem", fontWeight: 600,
          letterSpacing: "-0.01em", cursor: "pointer",
          fontFamily: FONT,
        }}
      >
        {cta}
      </button>
    </motion.div>
  );
}

// ─── Status stepper ──────────────────────────────────────────────────────────

const STEPS = [
  { id: "placed",      altId: "accepted",  label: "Placed",      Icon: CartCheck      },
  { id: "processing",                      label: "Processing",  Icon: HourglassSplit },
  { id: "enroute",                         label: "En Route",    Icon: Truck          },
  { id: "delivered",                       label: "Delivered",   Icon: Box            },
];

function OrderStatusTracker({ status }) {
  const currentStep = (() => {
    const s = status?.toLowerCase();
    if (s === "placed" || s === "accepted") return 0;
    const i = STEPS.findIndex((st) => st.id === s);
    return i === -1 ? 0 : i;
  })();

  return (
    <div style={{ display: "flex", alignItems: "flex-start", fontFamily: FONT }}>
      {STEPS.map((step, index) => {
        const done   = index <= currentStep;
        const active = index === currentStep;
        const StepIcon = step.Icon;

        return (
          <Fragment key={step.id}>
            {/* Dot + label */}
            <div
              style={{
                display: "flex", flexDirection: "column",
                alignItems: "center", gap: 6, flexShrink: 0,
              }}
            >
              <motion.div
                style={{
                  width: 24, height: 24, borderRadius: "50%",
                  background: done ? "#007AFF" : "rgba(60,60,67,0.09)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
                animate={{
                  boxShadow: active
                    ? "0 0 0 4px rgba(0,122,255,0.14)"
                    : "0 0 0 0px rgba(0,122,255,0)",
                }}
                transition={{ duration: 0.3 }}
              >
                {done ? (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 320, damping: 22 }}
                  >
                    <Check size={13} color="#fff" />
                  </motion.div>
                ) : (
                  <StepIcon size={11} color="rgba(60,60,67,0.38)" />
                )}
              </motion.div>
              <span
                style={{
                  fontSize: "0.65rem",
                  fontWeight: done ? 600 : 400,
                  color: done ? "#1C1C1E" : "rgba(60,60,67,0.38)",
                  letterSpacing: "-0.005em", whiteSpace: "nowrap",
                }}
              >
                {step.label}
              </span>
            </div>

            {/* Connector */}
            {index < STEPS.length - 1 && (
              <div
                style={{
                  flex: 1, height: 2, marginTop: 11,
                  background: index < currentStep ? "#007AFF" : "rgba(60,60,67,0.09)",
                  borderRadius: 1, transition: "background 0.4s ease",
                }}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

// ─── Order card ──────────────────────────────────────────────────────────────

function OrderHistoryItem({ id, cart, cost, createdAt, status }) {
  const [showModal, setShowModal] = useState(false);
  const total      = normalizeOrderCost(cost).total;
  const cfg        = getStatus(status);
  const itemCount  = cart?.length ?? 0;

  return (
    <>
      <motion.div
        whileHover={{ y: -2, boxShadow: "0 6px 24px rgba(0,0,0,0.07)" }}
        whileTap={{ scale: 0.975 }}
        transition={{ duration: 0.16 }}
        onClick={() => setShowModal(true)}
        style={{
          background: "#FFFFFF",
          borderRadius: 16,
          border: "1px solid rgba(60,60,67,0.07)",
          borderLeft: `3px solid ${cfg.color}`,
          cursor: "pointer",
          fontFamily: FONT,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "14px 16px 14px 14px",
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: 44, height: 44, borderRadius: 12,
            background: cfg.bg, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Receipt size={19} color={cfg.color} />
        </div>

        {/* Middle info — flex:1 + minWidth:0 allows date to truncate instead of overflow */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: "0 0 4px",
              fontSize: "0.875rem", fontWeight: 600,
              color: "#1C1C1E", letterSpacing: "-0.012em",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >
            Order #{id}
          </p>
          <div
            style={{
              display: "flex", alignItems: "center", gap: 4,
              fontSize: "0.75rem", color: "rgba(60,60,67,0.5)",
              overflow: "hidden",
            }}
          >
            <Clock size={10} style={{ flexShrink: 0 }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {formatFirebaseDate(createdAt)}
            </span>
            <span style={{ color: "rgba(60,60,67,0.25)", flexShrink: 0 }}>·</span>
            <span style={{ flexShrink: 0, whiteSpace: "nowrap" }}>
              {itemCount} item{itemCount !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Right: amount stacked above pill — fixed width so it never wraps */}
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <p
            style={{
              margin: "0 0 6px",
              fontSize: "1rem", fontWeight: 700,
              color: "#1C1C1E", letterSpacing: "-0.022em",
              whiteSpace: "nowrap",
            }}
          >
            {formatCurrency(total)}
          </p>
          <StatusPill cfg={cfg} />
        </div>
      </motion.div>

      <ShowModalMethod
        orderId={id}
        showModal={showModal}
        handleClose={() => setShowModal(false)}
        cost={cost}
        cartItems={cart}
        status={status}
      />
    </>
  );
}

// ─── Order detail modal ───────────────────────────────────────────────────────

function ShowModalMethod({ orderId, showModal, handleClose, cost, cartItems, status }) {
  const [isReordering, setIsReordering] = useState(false);
  const total = normalizeOrderCost(cost).total;
  const { cartItems: currentCart, increaseCartQuantity } = useShoppingCart();

  const handleReorder = async () => {
    try {
      setIsReordering(true);
      if (currentCart.length > 0) {
        toast.info("Please clear your cart before reordering.");
        return;
      }
      for (const item of cartItems) {
        await increaseCartQuantity(item.id, item.store, item.selectedOptions || {});
      }
      toast.success("Items added to cart!", 2500);
      handleClose();
    } catch (err) {
      console.error("Error reordering:", err);
      toast.error("Failed to reorder. Please try again.", 3000);
    } finally {
      setIsReordering(false);
    }
  };

  const sectionCard = {
    background: "rgba(242,242,247,0.65)",
    borderRadius: 14,
    padding: "14px 16px",
  };

  const actionBtn = (primary) => ({
    flex: 1, padding: "13px",
    borderRadius: 14, border: primary ? "none" : "1.5px solid rgba(0,122,255,0.3)",
    background: primary ? "#007AFF" : "transparent",
    color: primary ? "#fff" : "#007AFF",
    fontSize: "0.9375rem", fontWeight: 600,
    letterSpacing: "-0.01em",
    cursor: isReordering ? "not-allowed" : "pointer",
    fontFamily: FONT,
    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    opacity: primary && isReordering ? 0.55 : 1,
    transition: "opacity 0.15s ease",
  });

  return (
    <Modal show={showModal} onHide={handleClose} size="lg" centered scrollable>
      {/* Header */}
      <Modal.Header style={{ border: "none", padding: "18px 20px 10px", position: "relative", fontFamily: FONT }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 38, height: 38, borderRadius: 10,
              background: "rgba(0,122,255,0.09)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Receipt size={18} color="#007AFF" />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#1C1C1E", letterSpacing: "-0.02em" }}>
              Order Summary
            </p>
            <p style={{ margin: 0, fontSize: "0.73rem", color: "rgba(60,60,67,0.48)", letterSpacing: "-0.005em" }}>
              #{orderId}
            </p>
          </div>
        </div>
        <button
          onClick={handleClose}
          style={{
            position: "absolute", top: 16, right: 16,
            width: 28, height: 28, borderRadius: "50%",
            border: "none", background: "rgba(60,60,67,0.09)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", color: "#8E8E93", fontFamily: FONT,
          }}
        >
          <X size={13} />
        </button>
      </Modal.Header>

      {/* Body */}
      <Modal.Body style={{ padding: "4px 20px 8px", fontFamily: FONT }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Status tracker */}
          <div style={sectionCard}>
            <SectionLabel>Order Status</SectionLabel>
            <OrderStatusTracker status={status} />
          </div>

          {/* Items */}
          <div>
            <SectionLabel>Items</SectionLabel>
            <div>
              {cartItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.04 }}
                >
                  <OrderItem {...item} />
                  {index < cartItems.length - 1 && <Divider />}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Cost breakdown */}
          <div style={sectionCard}>
            <SectionLabel>Payment</SectionLabel>
            {getOrderCostLines(cost).map(({ label, value }) => (
              <div
                key={label}
                style={{
                  display: "flex", justifyContent: "space-between",
                  marginBottom: 9,
                }}
              >
                <span style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.52)" }}>
                  {label}
                </span>
                <span style={{ fontSize: "0.875rem", fontWeight: 500, color: value < 0 ? "#34C759" : "#1C1C1E" }}>
                  {formatSignedCurrency(value)}
                </span>
              </div>
            ))}
            <div style={{ height: 1, background: "rgba(60,60,67,0.08)", margin: "11px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#1C1C1E", letterSpacing: "-0.015em" }}>
                Total
              </span>
              <span style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#007AFF", letterSpacing: "-0.022em" }}>
                {formatCurrency(total)}
              </span>
            </div>
          </div>
        </div>
      </Modal.Body>

      {/* Footer */}
      <Modal.Footer style={{ border: "none", padding: "12px 20px 20px", fontFamily: FONT }}>
        <div style={{ display: "flex", gap: 10, width: "100%" }}>
          <button style={actionBtn(false)} onClick={handleClose}>
            Close
          </button>
          <button style={actionBtn(true)} onClick={handleReorder} disabled={isReordering}>
            {isReordering ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  style={{ width: 15, height: 15, borderWidth: 2 }}
                />
                Reordering…
              </>
            ) : (
              "Reorder"
            )}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Page root ────────────────────────────────────────────────────────────────

export function Orders() {
  const [user,              setUser]              = useState(null);
  const [orders,            setOrders]            = useState([]);
  const [isOrdersLoading,   setIsOrdersLoading]   = useState(true);
  const [authStateLoading,  setAuthStateLoading]  = useState(true);
  const [retryCount,        setRetryCount]        = useState(0);
  const navigate   = useNavigate();
  const MAX_RETRIES = 3;

  const fetchOrders = useCallback(async () => {
    if (!user?.uid) {
      setIsOrdersLoading(false);
      return;
    }
    try {
      setIsOrdersLoading(true);
      const idToken = await user.getIdToken();
      const data = await fetchOrdersHistory(user.uid, idToken);
      if (!data) throw new Error("No orders data received");
      setOrders(data);
      setRetryCount(0);
    } catch (err) {
      console.error("Error fetching orders:", err);
      if (retryCount < MAX_RETRIES) {
        setRetryCount((prev) => prev + 1);
        setTimeout(() => fetchOrders(), 1000 * retryCount);
      }
    } finally {
      setIsOrdersLoading(false);
    }
  }, [user, retryCount]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) {
        setUser(u);
      } else {
        setIsOrdersLoading(false);
      }
      setAuthStateLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (user) fetchOrders();
  }, [user, fetchOrders]);

  const handleRetry = useCallback(() => {
    if (!user?.uid) return;
    setRetryCount(0);
    fetchOrders();
  }, [fetchOrders, user]);

  return (
    <PullToRefreshWrapper onRefresh={fetchOrders}>
    <Seo {...generateNoIndexSEO("Your Orders")} />
    <div style={{ fontFamily: FONT }}>
      <Container className="py-3 px-3 px-md-4" style={{ maxWidth: 720 }}>
        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            marginBottom: 20, paddingTop: 4,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <IconBtn onClick={() => navigate(-1)}>
              <ArrowLeft size={17} color="#1C1C1E" />
            </IconBtn>
            <h1
              style={{
                margin: 0, fontSize: "1.125rem", fontWeight: 700,
                color: "#1C1C1E", letterSpacing: "-0.025em",
              }}
            >
              Order History
            </h1>
          </div>
          <IconBtn
            onClick={handleRetry}
            disabled={isOrdersLoading}
            tint="rgba(0,122,255,0.09)"
          >
            <ArrowClockwise size={16} color="#007AFF" />
          </IconBtn>
        </motion.div>

        {/* ── Body ── */}
        {authStateLoading ? (
          <CenteredSpinner />
        ) : user ? (
          isOrdersLoading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[...Array(4)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.22, delay: i * 0.06 }}
                >
                  <OrderSkeleton />
                </motion.div>
              ))}
            </div>
          ) : orders.length === 0 ? (
            <EmptyState
              icon={<Receipt size={26} color="#007AFF" />}
              title="No orders yet"
              body="When you place an order, it'll appear here"
              cta="Browse Stores"
              onCta={() => navigate("/")}
            />
          ) : (
            <Row className="g-3">
              <AnimatePresence>
                {orders.map((item, index) => (
                  <Col key={item.id} xs={12}>
                    <motion.div
                      initial={{ opacity: 0, y: 14, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.96 }}
                      transition={{ duration: 0.26, delay: Math.min(index * 0.05, 0.3) }}
                    >
                      <OrderHistoryItem {...item} />
                    </motion.div>
                  </Col>
                ))}
              </AnimatePresence>
            </Row>
          )
        ) : (
          <EmptyState
            icon={<PersonCircle size={28} color="#007AFF" />}
            title="Sign in to continue"
            body="View your order history and track deliveries"
            cta="Sign In"
            onCta={() => navigate("/login")}
          />
        )}
      </Container>
    </div>
    </PullToRefreshWrapper>
  );
}
